import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Timer } from "lucide-react";

export function PaymentAlertTimeDialog({
  isOpen,
  onOpenChange,
  deviceName,
  initialAlertTime = 30,
  onConfirm,
  loading = false,
}) {
  const [alertTime, setAlertTime] = useState(initialAlertTime || 30);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setAlertTime(initialAlertTime || 30);
      setError("");
    }
  }, [isOpen, initialAlertTime]);

  const handleClose = () => {
    setError("");
    onOpenChange(false);
  };

  const handleValueChange = (val) => {
    const num = Number(val);
    setAlertTime(val);

    if (isNaN(num)) {
      setError("Please enter a valid number");
    } else if (num < 20) {
      setError("Minimum payment alert time is 20 seconds");
    } else if (num > 90) {
      setError("Maximum payment alert time is 90 seconds");
    } else {
      setError("");
    }
  };

  const handlePresetSelect = (seconds) => {
    setAlertTime(seconds);
    setError("");
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const num = Number(alertTime);

    if (isNaN(num) || num < 20 || num > 90) {
      setError("Alert time must be between 20 and 90 seconds");
      return;
    }

    await onConfirm?.(num);
  };

  const presets = [20, 30, 45, 60, 90];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-full bg-yellow-500/10 text-yellow-500">
              <Timer size={20} />
            </div>
            <div>
              <DialogTitle>Payment Alert Time</DialogTitle>
              <DialogDescription>
                {`Configure alert duration for ${deviceName || "Pax Terminal"}.`}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="alert-time" className="text-sm font-medium">
              Alert Time (seconds)
            </Label>
            <div className="relative">
              <Input
                id="alert-time"
                type="number"
                min={20}
                max={90}
                value={alertTime}
                onChange={(e) => handleValueChange(e.target.value)}
                placeholder="20 - 90"
                className={`pr-12 ${error ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                required
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground font-medium pointer-events-none">
                sec
              </span>
            </div>
            {error ? (
              <p className="text-xs text-red-500 mt-1">{error}</p>
            ) : (
              <p className="text-xs text-muted-foreground mt-1">
                Select or enter a duration between 20s and 90s.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground font-medium">Quick Select</Label>
            <div className="flex flex-wrap gap-2">
              {presets.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => handlePresetSelect(preset)}
                  className={`px-3 py-1 text-xs rounded-md border transition-all ${
                    Number(alertTime) === preset
                      ? "bg-cyellow text-black border-cyellow font-semibold shadow-sm"
                      : "bg-accent/40 hover:bg-accent border-border/50 text-foreground"
                  }`}
                >
                  {preset}s
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-2 pt-2 border-t border-border/50">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-cyellow text-black hover:bg-yellow-400 font-medium"
              disabled={!!error || !alertTime || loading}
            >
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
